/**
 * The sidebar's information architecture:
 * Monitor → Onboard & manage → Engineer → Comply → Administer.
 *
 * This grouping encodes how the operator's work actually flows and should not be reordered
 * casually — it is the product's mental model, not a menu.
 * See Docs/06-design/DESIGN-SYSTEM.md §6.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY "ONBOARD & MANAGE" IS ITS OWN GROUP, IN THAT ORDER
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Bringing a site onto the platform is ONE job done in THREE stages, and the rail used to
 * scatter them: `Sites` and `Devices` sat beside `SLD Studio`, and `Parameters` — the screen
 * where you say what a reading MEANS — sat below `Calculators`, with nothing to say that the
 * three are consecutive steps of the same task. Somebody onboarding a site had to already
 * know the order to follow it.
 *
 * The stages, and the question each answers:
 *
 *   Onboarding         WHAT is this site?      identity, industry, electrical envelope
 *   Integrations       WHAT is talking to us?  feeds, discovered devices, the data gate
 *   Device management  WHAT does it MEAN?      parameters, units, value labels, monitoring
 *
 * Each is a container with its own subpages, so the group stays three entries wide however
 * much lives underneath it. `Sites` leads the group because every one of the three is scoped
 * to a site, and choosing one is where the work starts.
 *
 * `Engineer` keeps what is genuinely engineering rather than onboarding: drawing the power
 * chain, and the standalone calculators.
 */
import type { IconName } from '@/components/lumen/Icon';
import type { SiteScope } from './site-context-value';

export interface NavItem {
  id: string;
  label: string;
  icon: IconName;
  /**
   * Whether this screen follows the header's ACTIVE SITE. Required, not optional, so every
   * screen has to decide — the header selector reads it to say, on every page, whether the
   * site it names is what the page is showing. Before this existed the selector's hover text
   * listed five screens, three of which no longer matched the code, and an operator on the
   * fleet Alarms board had no way to know the header site was not what they were looking at.
   * See `SiteScope`.
   */
  siteScope: SiteScope;
  /**
   * The permission slug this screen needs before it is worth OFFERING, or absent when the screen
   * is a read anybody may do.
   *
   * ─────────────────────────────────────────────────────────────────────────────
   * WHY THE RAIL NEEDS THIS AT ALL
   * ─────────────────────────────────────────────────────────────────────────────
   *
   * Measured 2026-09-18 by signing in as all four real roles: every one of them got the SAME
   * 23-entry rail. An Operator holding two permissions — `alarms.triage` and `users.read` — was
   * offered Access, Audit trail, SLD Studio, Onboarding and Device management exactly as the
   * administrator was, and every one of those answered 403 on arrival.
   *
   * The API was never wrong; it refused all of them. What was wrong is the offer, and
   * `lumen-fe/CLAUDE.md` says so: *"Hide what a role cannot do — a button the API then refuses
   * reads as a broken product, not as a permission the user lacks."*
   *
   * ── WHAT IT IS NOT ───────────────────────────────────────────────────────────
   * NOT a second authorisation table. The slug named here is the same slug the route enforces
   * with, and the caller's set arrives from `GET /me` — `permissions`, resolved per request from
   * `role_permissions`, the one the routes read. This decides what to DRAW; it decides nothing
   * about what is allowed, and a rail that got it wrong could only ever hide a working screen,
   * never reveal a forbidden one.
   *
   * ── AND WHY MOST ITEMS STILL HAVE NONE ───────────────────────────────────────
   * Absent means "anybody signed in may look". Reading the fleet, the alarm board, a site's
   * detail or Your account is the product's whole value to a `sales` role that may change
   * nothing — hiding those would confuse read-only with no-access. The rule is: name a
   * permission only where the screen's PURPOSE is the act. A screen you can read and not edit
   * stays; a screen that is nothing but an edit goes.
   *
   * `writes` is deliberately not listed. Its visibility is an ORGANISATION gate as well as a
   * permission (ADR-0014), so it stays in the separate `hiddenItems` computation that already
   * AND-s the two — see `App.tsx`. Folding it in here would lose the distinction between "your
   * role cannot" and "this organisation has it switched off".
   *
   * ── 2026-09-30, AND WHY FOUR MORE GAINED ONE ─────────────────────────────────
   * Findings, Compliance, Standards, Reports and Calculators are the exception to the paragraph
   * above, and it is worth saying so rather than leaving the old reasoning to read as still
   * true: production needed to be able to HIDE a section per role and per organisation, and
   * these five had no `needs` slug to hide behind — Onboarding's only candidate, `sites.edit`,
   * would also have stopped editing an existing site. Migration 0083 named the reading acts
   * (`compliance.read`, `findings.read`, `calculators.use`) and split site creation from site
   * editing (`sites.create`) rather than inventing a permission per screen — see that
   * migration's header for why this is not the `pages.*` proposal `PERMISSIONS.md` §5.1
   * rejected. Every role held all four on the day this shipped, so nothing changed for anyone.
   */
  needs?: string;
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

export const NAV_GROUPS: NavGroup[] = [
  /*
   * ORDERING PRINCIPLE, within each group: what DEMANDS ACTION comes before what merely
   * informs, and what is used often comes before what is used occasionally.
   *
   * The previous order was the order these modules were built in, which put Telemetry — an
   * investigative tool you reach for after something else told you to — above Alarms, the one
   * item in Monitor that means somebody has to do something now. An operator scanning a rail
   * top-to-bottom should meet the fleet's state, then its faults, then its gaps, and only then
   * the tools for digging in.
   *
   * This diverges from the module listing in CLAUDE.md, which reflects the nine modules as
   * SPECIFIED rather than as ranked, and which predates Telemetry, Alarms, Devices and
   * Parameters existing at all. The grouping — Monitor / Engineer / Comply — is unchanged, and
   * that grouping is the part the working agreement actually fixes.
   */
  {
    label: 'Monitor',
    items: [
      /*
       * The landing. Opens on the SELECTED site's posture with a Fleet toggle (Hamzah,
       * 2026-09-09); the site table and the map stay fleet-wide in both modes because they are
       * how the selection is changed.
       */
      {
        id: 'overview',
        label: 'Overview',
        icon: 'ic-gauge',
        siteScope: 'site',
      },
      /*
       * THE EXECUTIVE SCREEN (prototype v2, 2026-11-02): one screen, no tabs — the network now, its
       * sites ranked, what needs you, the value — for the reader who wants the answer, not the plant.
       * Right after the Overview it summarises, gated as the Overview is (no `needs`), and over the
       * whole fleet rather than one site. Its view is `modules/executive/`.
       */
      { id: 'executive', label: 'Executive', icon: 'ic-target', siteScope: 'fleet' },
      /*
       * Alarms second, not fourth. It is the only Monitor entry that means something is wrong
       * right now, and it carries a count — an item with a badge buried below one without a
       * badge inverts the rail's own signal.
       */
      { id: 'alarms', label: 'Alarms', icon: 'ic-bell', siteScope: 'site' },
      /*
       * Work orders right after Alarms (2026-10-12): the other half of "somebody has to do
       * something" — what a person was asked to do, where Alarms is what the site raised. It was
       * the Work tab of Alarms; with analytics, a table and its own log it outgrew a tab, and an
       * order that never came from an alarm (a PM round, a warranty claim) no longer files under
       * Alarms (`modules/workorders/view-tab.ts`). Site-scoped like Alarms: the header site narrows
       * every view. No `needs`: reading the work is anybody's, as the API's reads are; raising and
       * moving orders is gated per control.
       */
      { id: 'work-orders', label: 'Work orders', icon: 'ic-calendar', siteScope: 'site' },
      /* Seeing the plant. Context for the alarm board above. */
      {
        id: 'twin',
        label: 'Digital Twin',
        icon: 'ic-server',
        siteScope: 'site',
      },
      /* Reading the numbers. Reached because something above sent you here. */
      {
        id: 'telemetry',
        label: 'Telemetry',
        icon: 'ic-activity',
        siteScope: 'site',
      },
    ],
  },
  {
    label: 'Onboard & manage',
    items: [
      /* The fleet, and the entry point: the stages below are scoped to a site. Creating one
         (Onboarding, stage 1) is no longer a rail entry (Hamzah, 2026-10-26): Sites' own
         "Onboard a site" opens it, so the rail named one task twice. Its gate is
         `OFF_RAIL_NEEDS` below. */
      { id: 'sites', label: 'Sites', icon: 'ic-building', siteScope: 'fleet' },
      /*
       * Stage 2 — WHAT IS TALKING TO US. Feeds, discovered devices, the integration gate.
       *
       * Badged, because a pending device is ACTIVELY DISCARDING data. It is a queue with a
       * running cost, not a reference list, so the count has to be visible from every page
       * rather than buried inside a site. Fleet-scoped: an unassigned feed belongs to no site
       * yet, so a site filter would hide the very queue this screen exists for.
       */
      {
        id: 'integrations',
        /* The data gate: assigning a discovered device to a site, which is the whole screen. */
        needs: 'devices.integrate',
        label: 'Integrations',
        icon: 'ic-plug',
        siteScope: 'fleet',
      },
      /* Stage 3 — WHAT DOES IT MEAN. Parameters, units, value labels, monitoring. */
      {
        id: 'devices',
        /* Parameters, units and value labels — every control here writes metric config. */
        needs: 'metrics.edit',
        label: 'Device management',
        icon: 'ic-server',
        siteScope: 'site',
      },
      /* The Playground (0117): demo sites whose readings are generated, for showing the product.
         Last in the group because it onboards nothing real; admins and sales only. */
      { id: 'playground', needs: 'simulations.run', label: 'Playground', icon: 'ic-zap', siteScope: 'none' },
    ],
  },
  {
    label: 'Engineer',
    items: [
      /* Draw the power chain. Depends on devices having roles and parents. */
      {
        id: 'topology',
        /* An editor. The read-only single line lives on the site hub, which stays visible. */
        needs: 'topology.edit',
        label: 'SLD Studio',
        icon: 'ic-route',
        siteScope: 'site',
      },
      /*
       * Writing to live hardware (ADR-0014). Declared here but HIDDEN by the shell unless
       * `me.can.proposeWrites || me.can.approveWrites` — the org gate defaults OFF, and a
       * rail entry for a feature the organisation has not enabled is an invitation to ask
       * "how do I turn this on" about the most dangerous act in the product. Fleet-scoped
       * today: it carries its own site filter for the approver's queue.
       */
      {
        id: 'writes',
        label: 'Remote control',
        icon: 'ic-sliders',
        siteScope: 'fleet',
      },
      /* Standalone tools. Prefilled from the selected site's parameters. */
      {
        id: 'calculators',
        /* A read of a pure, stateless tool (0083) — held apart only so an organisation can
           choose to hide it, same as the three below. */
        needs: 'calculators.use',
        label: 'Calculators',
        icon: 'ic-calc',
        siteScope: 'site',
      },
    ],
  },
  {
    label: 'Comply',
    items: [
      /*
       * Findings FIRST in Comply (moved from Monitor, Hamzah 2026-09-30). They are derived
       * compliance gaps — the one thing in this group that asks for action, badged — so the
       * group's own rule puts them ahead of the matrix and the library they come from. Monitor
       * is for what the site is doing now; a finding is what its declaration is missing.
       */
      {
        id: 'findings',
        /* 0083 — first permission this screen has ever had; see the migration header for why. */
        needs: 'findings.read',
        label: 'Findings',
        icon: 'ic-alert',
        siteScope: 'site',
      },
      /*
       * Compliance before Standards. "Am I compliant?" is asked far more often than "what does
       * IEC 62305 say?" — the matrix is the answer and the library is the reference behind it.
       */
      {
        id: 'compliance',
        /* 0083 — see the migration header for why this screen gained a `needs` at all. */
        needs: 'compliance.read',
        label: 'Compliance',
        icon: 'ic-check2',
        siteScope: 'site',
      },
      {
        id: 'standards',
        needs: 'compliance.read',
        label: 'Standards',
        icon: 'ic-library',
        siteScope: 'site',
      },
      /* The artefact you hand to an auditor. The end of the workflow, so the end of the group. */
      { id: 'reports', needs: 'compliance.read', label: 'Reports', icon: 'ic-file', siteScope: 'site' },
    ],
  },
  {
    /*
     * A FOURTH GROUP rather than items appended to Comply.
     *
     * Who has access and what they did is not a compliance artefact — it is the record of
     * how the console itself was used, and it is reached for a different reason and usually
     * by a different person. Filing it under Comply would put "invite a colleague" beside
     * "IEC 62305", which describes neither.
     *
     * Last, because it is administration: consulted occasionally, and never the reason
     * somebody opened the product.
     */
    label: 'Administer',
    items: [
      {
        id: 'users',
        /*
         * ─────────────────────────────────────────────────────────────────────────────
         * `users.read`, AND THE REVERT IS THE INTERESTING PART
         * ─────────────────────────────────────────────────────────────────────────────
         *
         * This was moved to `users.invite` for one session, to answer *"why can an engineer see
         * this?"* — every one of the five system roles holds `users.read`, so an Operator with
         * two permissions was offered an ADMINISTER entry. The complaint was real. The remedy
         * was wrong, and the note left behind here said so without noticing: it claimed the
         * intent recorded in `modules/users/permissions.ts` — that "an operator holds
         * `users.read` so they can see WHO their colleagues are" — now "has no home", and that
         * "a directory is a different page with different content".
         *
         * It is not a different page. IT IS THIS PAGE, AND THIS PAGE ALREADY DEGRADES.
         * `UsersView` computes `mayAdminister = mine.includes('users.invite')` and reads it in
         * nine places: Invite, the role dropdowns, Remove, Send reset and the reach editor are
         * all absent without it. The backend does the matching half — `users/service.ts` withholds
         * sign-in history from anybody lacking `users.invite`, with a comment saying an operator
         * holds `users.read` precisely so they can see who their colleagues are. Both halves of
         * the read-only directory were already built; gating the rail on `users.invite` was the
         * one thing standing between an operator and the screen written for them.
         *
         * So the entry is back on `users.read`, which every role but Sales holds, and the PAGE
         * TITLE carries the difference instead — "Users & access" for somebody who can administer,
         * "Your team" for somebody who cannot. The complaint was about PLACEMENT, not access: an
         * administration group is the wrong shelf for a directory, and that is a question about
         * where this entry sits, which is not settled by hiding it.
         *
         * The rail LABEL stays "Users & access" for both, and that is a known remainder rather
         * than an oversight: `NavItem.label` is a constant read by a rail that has no permissions
         * in scope, and threading the caller's set into the information architecture to retitle
         * one entry is a larger change than the honesty it buys. The page says the right thing on
         * arrival.
         */
        needs: 'users.read',
        label: 'Users & access',
        icon: 'ic-shield',
        siteScope: 'none',
      },
      /*
       * Performance sits under Users & access and above Access: it is the next question after
       * "who is here" — how are they getting on — and it is about PEOPLE, where Access is about
       * structure (organisations, roles, the ceiling). Structure after people, as the note
       * above says. `performance.read` is held by the admin template only (0094): a rating of
       * named colleagues is read by whoever manages them, and the rated do not see the table
       * they are in.
       */
      {
        id: 'performance',
        needs: 'performance.read',
        label: 'Performance',
        icon: 'ic-activity',
        siteScope: 'none',
      },
      /*
       * Access follows the two people screens (Users, then Performance) because it answers the question the people list
       * provokes: you look at who is here, then at what the roles they hold actually permit,
       * and then at the organisation those roles belong to. Structure after people, not before
       * — nobody opens a console wanting to see a hierarchy.
       */
      {
        id: 'access',
        /* Organisations, roles, departments and limits. Nothing on it is readable without
           `roles.manage`; the whole screen 403s for anybody else. */
        needs: 'roles.manage',
        label: 'Access',
        icon: 'ic-layers',
        siteScope: 'none',
      },
      /* The trail second: it is what you read AFTER wondering about a change above. */
      {
        id: 'audit',
        /* The audit trail is the one read that is itself privileged — see ADR-0001. */
        needs: 'audit.read',
        label: 'Audit trail',
        icon: 'ic-library',
        siteScope: 'none',
      },
      /*
       * ── THE ORGANISATION'S OWN SETTINGS, AND WHY THEY ARE NOT IN ACCESS ────────
       *
       * Appearance was a tab inside the Access drawer, which put it behind `roles.manage`: the
       * Access screen refuses anybody without it, and branding needs `tenants.edit` and 0058's
       * ancestor rule instead. An administrator entitled to brand their own console could only
       * reach the control through a screen they might not be allowed to open.
       *
       * It sits HERE, before Your account and Settings, because the group runs acts, then reads,
       * then settings — and this puts the settings screens beside each other: what the
       * ORGANISATION decided about itself, then what YOU decided about yourself. It scopes from the header, which is
       * the scope branding actually wants: a tenant is branded from inside the tenant's own
       * screen with the header pointed at them.
       */
      {
        id: 'organisation',
        needs: 'tenants.edit',
        label: 'Organisation',
        icon: 'ic-shield',
        siteScope: 'none',
      },
      /* Not a page of its own since 2026-11-03: opening it lands on Settings › Account
         (`App.tsx` redirects `profile`). Kept here because people look for "Your account". */
      {
        id: 'profile',
        label: 'Your account',
        icon: 'ic-gauge',
        siteScope: 'none',
      },
      /*
       * SETTINGS — the look, the layout, accessibility, notifications: what YOU decided about how
       * the console behaves for you. Last, beside Your account, as the reference design has it.
       * It was reachable only from the account menu's Appearance row, so Apps, ⌘K, the dock's
       * "Add from Apps" and the rail — all of which read this list — had no way to it (Hamzah,
       * 2026-11-03: "why there's no settings as a route"). No permission: it is personal.
       */
      {
        id: 'settings',
        label: 'Settings',
        icon: 'ic-sliders',
        siteScope: 'none',
      },
    ],
  },
];

/**
 * How the CURRENT tab relates to the header site, including the routable ids that are not
 * rail entries. `site-detail` is the site hub and `metrics` is the Parameters screen, which
 * seeds its filter from the selection — both follow it. Anything unknown is `none`, which
 * makes the selector say so rather than claim a scope the code does not implement.
 */
export function siteScopeOf(tab: string): SiteScope {
  if (tab === 'site-detail' || tab === 'metrics') return 'site';
  for (const g of NAV_GROUPS) {
    const item = g.items.find((i) => i.id === tab);
    if (item) return item.siteScope;
  }
  return 'none';
}

/**
 * Whether the PLANT selector means anything on a screen — a different axis from `siteScope`,
 * and it must have its own table rather than being derived from that one.
 *
 * THE TRAP THIS AVOIDS, because it was nearly written the other way. Overview is
 * `siteScope: 'site'` — it genuinely does follow the header site. Deriving plant
 * applicability from that would render the plant control LIVE on Overview, which is the
 * precise opposite of what Overview should do: it is a WARNING surface, and a filter may
 * narrow what you are browsing but never what would warn you. Its map and site table are
 * also the SELECTION MECHANISM, so narrowing them would make it impossible to select a site
 * that has no plants — every site but one today. And a plant has no compliance verdict at
 * all: `scope.ts` decides applicability by archetype group, archetype is a column on
 * `sites`, so a plant-scoped posture band would assert a number the engine cannot compute.
 *
 * A screen is therefore site-scoped and plant-none at the same time, and that is not a
 * contradiction — the two axes answer different questions.
 *
 * Listed here are the screens where choosing a plant CHANGES WHAT YOU SEE. `alarms` and
 * `metrics` are on the list ahead of their implementation, deliberately: the work is
 * assigned and in progress, and a control that dims on Tuesday and lights on Wednesday
 * teaches an operator that the product is unreliable. Everything else resolves to false and
 * the control DIMS — never hides — for the same reason the site selector dims: the selection
 * still governs the next screen the operator opens, so removing the control would hide the
 * state that decides it.
 */
const PLANT_SCOPED_TABS: ReadonlySet<string> = new Set([
  'twin',
  'topology',
  'telemetry',
  'alarms',
  'devices',
  'metrics',
  /*
   * The site hub, and this one entry is DELIBERATELY IMPRECISE — six of its eight sub-tabs
   * narrow by plant (SLD, Parameters, Alarms, Analytics, Devices, Compare) and two do not
   * (Compliance parameters, Detail), because compliance is graded per SITE — `scope.ts` keys
   * applicability on archetype and archetype is a column on `sites` — and Detail is the
   * site's own identity and location.
   *
   * The alternative was plumbing the hub's sub-page up into the header so this could be
   * exact. It was rejected: the sub-page is local state in `SiteHubView` mirrored to the URL
   * with `replaceState`, which fires no event, so the header would need either a context of
   * its own or a poll — real machinery to remove a control's glow from two screens where a
   * reader is not confused anyway. Nobody looks at a site's street address and wonders why
   * choosing a plant did not change it.
   *
   * Recorded so this is a decision with a cost attached rather than a gap someone later
   * assumes was an oversight.
   */
  'site-detail',
]);

export function plantScopeOf(tab: string): boolean {
  return PLANT_SCOPED_TABS.has(tab);
}

/**
 * Screens reached from another screen rather than the rail, with the permission each needs —
 * gated exactly as a rail entry's `needs` is (App's `hiddenItems`), so a bookmarked link to one
 * a role may not use falls back to the Overview instead of 403ing inside it.
 *
 * `onboarding`: creating a site IS the screen — `sites.create` (0083) is what `POST /sites`
 * enforces. Split from `sites.edit`, which still governs an existing site and would otherwise
 * have to be pulled to hide this screen, stopping every existing site's edit screen with it.
 */
export const OFF_RAIL_NEEDS: Readonly<Record<string, string>> = {
  onboarding: 'sites.create',
};

/**
 * Tab id -> its sidebar label, for anything that needs to NAME the current screen.
 *
 * Derived from `NAV_GROUPS` rather than written twice: a second literal drifts the moment
 * a label is reworded, and the error boundary's whole job is to say which screen failed.
 * The ids that are not in the rail (`site-detail`, `onboarding`) are added explicitly —
 * they are reachable and can crash like anything else.
 */
export const NAV_LABEL: Record<string, string> = {
  ...Object.fromEntries(NAV_GROUPS.flatMap((g) => g.items.map((i) => [i.id, i.label]))),
  'site-detail': 'Site',
  onboarding: 'Onboard a site',
  /* One person's Performance page (`/performance/person?id=`, 2026-10-12): not a rail entry. */
  'performance-person': 'Person',
  /* `metrics` is now a SUBPAGE of Device management rather than a rail entry, but it is
     still a routable id and can still crash, so the error boundary must be able to name it. */
  metrics: 'Parameters',
};
